import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AxiosError } from 'axios';
import { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLocalizeMessage } from 'libs.nucleus';
import { PageHeader, PageWrapper, useBooleanState } from 'libs.react';
import { WorkspaceSiteModal, WorkspaceSitesTable, DeleteWorkspaceSitesModal, ValidationErrorModal, } from '../../components';
import { SiteDeleteErrorCode } from '../../components/delete_workspace_sites_modal';
import { useDeleteWorkspaceSites } from '../../hooks/use_delete_workspace_sites';
export const WorkspaceSiteList = () => {
    const translate = useLocalizeMessage();
    const [isCreationModalOpen, setCreationModalOpen, setCreationModalClose] = useBooleanState();
    const [selectedRowIds, setSelectedRowIds] = useState([]);
    const [isDeleteModalOpen, openDeleteModal, closeDeleteModal] = useBooleanState(false);
    const [isValidationErrorModalOpen, openValidationErrorModal, closeValidationErrorModal] = useBooleanState(false);
    const [invalidSites, setInvalidSites] = useState([]);
    const [validationErrorMessage, setValidationErrorMessage] = useState('');
    const [selectedSitesCache, setSelectedSitesCache] = useState(new Map());
    const { deleteWorkspaceSitesAsync, isDeleting } = useDeleteWorkspaceSites();
    // Get selected sites from cache (persists across pagination)
    const selectedSites = useMemo(() => Array.from(selectedSitesCache.values()), [selectedSitesCache]);
    // Type guard to check if error data is a site deletion validation error
    const isSiteDeleteValidationError = (data) => {
        return (typeof data === 'object' &&
            data !== null &&
            'errCode' in data &&
            data.errCode === SiteDeleteErrorCode.INVALID_ARGUMENT_SITE &&
            'details' in data &&
            Array.isArray(data.details));
    };
    const handleRowSelectionChange = useCallback((newSelectedIds, currentPageSites) => {
        setSelectedRowIds(newSelectedIds);
        // Update cache: add newly selected from current page, remove deselected
        setSelectedSitesCache((prev) => {
            const updated = new Map(prev);
            // Add/update sites from current page that are selected
            currentPageSites.forEach((site) => {
                if (newSelectedIds.includes(site.id.toString())) {
                    updated.set(site.id, site);
                }
            });
            // Remove sites that are no longer selected
            Array.from(updated.keys()).forEach((id) => {
                if (!newSelectedIds.includes(id.toString())) {
                    updated.delete(id);
                }
            });
            return updated;
        });
    }, []);
    const handleDeleteSuccess = useCallback(() => {
        setSelectedRowIds([]); // Clear selection
        setSelectedSitesCache(new Map()); // Clear cache
        // Note: Query invalidation is handled by the deletion hook
    }, []);
    const handleValidationError = useCallback((sites, errorMessage) => {
        setInvalidSites(sites);
        setValidationErrorMessage(errorMessage);
        closeDeleteModal(); // Close confirm modal before opening validation modal
        openValidationErrorModal();
    }, [closeDeleteModal, openValidationErrorModal]);
    const handleDeleteSites = useCallback(async (sitesToDelete) => {
        const payload = {
            siteIds: sitesToDelete.map((site) => site.id),
        };
        try {
            await deleteWorkspaceSitesAsync(payload);
            handleDeleteSuccess();
            return true; // Success
        }
        catch (error) {
            // Check for validation errors
            if (error instanceof AxiosError) {
                const errorData = error.response?.data;
                if (isSiteDeleteValidationError(errorData)) {
                    // TypeScript now knows errorData is SiteDeleteErrorResponse
                    // Update validation error state and show modal
                    handleValidationError(errorData.details, errorData.message || 'Cannot delete sites');
                    return false; // Validation error handled - confirm modal already closed by handleValidationError
                }
            }
            // For other errors, hook already handled notification
            return false;
        }
    }, [deleteWorkspaceSitesAsync, handleDeleteSuccess, handleValidationError]);
    const handleDeleteValidSites = useCallback(async () => {
        const invalidSiteIds = new Set(invalidSites.map((site) => site.id));
        const validSites = selectedSites.filter((site) => !invalidSiteIds.has(site.id));
        const success = await handleDeleteSites(validSites);
        if (success) {
            closeValidationErrorModal();
        }
        return success;
    }, [invalidSites, selectedSites, handleDeleteSites, closeValidationErrorModal]);
    const headerOptions = [
        {
            name: translate('Create site'),
            variant: 'primary',
            onClick: setCreationModalOpen,
        },
    ];
    return (_jsxs(PageWrapper, { children: [_jsx(PageHeader, { title: translate('Sites'), subtitle: translate('Create and manage sites in your workspace.'), options: headerOptions, LinkComponent: Link }), _jsx(WorkspaceSitesTable, { selectedIds: selectedRowIds, onRowSelectionChange: handleRowSelectionChange, actionBarProps: {
                    actionButtons: [
                        {
                            label: translate('Delete sites'),
                            onClick: openDeleteModal,
                        },
                    ],
                } }), isCreationModalOpen && _jsx(WorkspaceSiteModal, { onClose: setCreationModalClose }), isDeleteModalOpen && (_jsx(DeleteWorkspaceSitesModal, { selectedSites: selectedSites, onClose: closeDeleteModal, onDelete: handleDeleteSites, isDeleting: isDeleting })), isValidationErrorModalOpen && (_jsx(ValidationErrorModal, { selectedSites: selectedSites, invalidSites: invalidSites, errorMessage: validationErrorMessage, onClose: closeValidationErrorModal, onDeleteValidSites: handleDeleteValidSites, isDeleting: isDeleting }))] }));
};
